/**
 * New material / edit material — the first form on the kit.
 *
 * `docs/record-flyouts.md` §4.1, drawn from
 * `24-record-flyouts/html/flyout-new-material.html`. Four sections in the
 * order somebody thinks about a material: what it is, what it costs and how
 * far one goes, what it sells for, and where it may be used.
 *
 * It replaces `items-list.tsx`'s `ItemEditor`, a 540px `SlideOver` built out
 * of four page-local components — `NumberField`, `UnitSelect`, `Select`, and
 * two `Card`s pretending to be bands. All four are gone; everything here
 * comes from `components/form/`.
 *
 * Three things the old form could not do:
 *
 * **Make a category without leaving.** The Category field is a `ComboInput`
 * whose last row creates what you typed. The old `Select` said "Categories
 * are set up in the Categories tab", which is a form telling you to go
 * somewhere else and come back.
 *
 * **Say what the numbers mean.** The cost-and-allocation sentence is a green
 * `Band` with the worked example under it, not a grey card; the three
 * resulting numbers are a `Readout`, not three stacked labels.
 *
 * **Save and add another.** A price list is entered one row after another,
 * and the fields that repeat — category, vendor, both units, the pricing mode
 * — carry over and say so.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  buyingSentence, buyingStock, buyingUnitWord, marginPctOf, poLineWords, type BuyingRule, type OrgUnitSummary,
} from '@rowted/shared'
import { Band, Card, SectionLabel, Tag } from '../primitives.js'
import {
  CheckRow, ComboInput, Field, FormGrid, FormSection, MoneyInput, NumberInput, PairField,
  Readout, ReadonlyInput, SelectInput, TextInput, Wide,
} from '../form/index.js'
import { request } from '../../lib/api.js'
import { usePhone } from '../../lib/use-narrow.js'
import { formatNumber, formatRate, formatRelative, formatUnitCost } from '../../lib/format.js'
import {
  CatalogSheet, CustomerNameField, NotesField, PricedByField, SegmentedPicker, TryItPanel,
  UnitSelect, WhereAndOptions, amountText, marginTone, percentText,
} from './catalog-parts.js'
import { createCategory, createUnit, useCatalogLookups } from './catalog-lookups.js'
import {
  ALLOCATED_IN, BAND_HINT, BUY_LABEL, CATEGORY, LAST_PAID, MARGIN, MARKUP_ON_COST,
  MINIMUM_HINT, MINIMUM_LABEL, NAME, SKU, SKU_HINT, TRACK_STOCK, TRACK_STOCK_SUB, USUAL_VENDOR,
  WHAT_IT_COSTS, WHAT_IT_COSTS_SUB, WHAT_IT_IS, WHAT_IT_SELLS_FOR, costPerLabel, costSlash,
  perLabel, pricePer, ruleHint, ruleLabel, ruleUsed, ruleWhole, sellsSlash,
  STOCK_LEFT, STOCK_RECEIVED, STOCK_TITLE, STOCK_USED,
} from './catalog-copy.js'

/** How the sell price is decided. One question, three answers, never two fields. */
type Pricing = 'company' | 'markup' | 'fixed'

export interface MaterialForm extends Record<string, unknown> {
  id?: string
  name: string
  /** Customer-facing name (0126); null is "the same as the name". */
  customerName: string | null
  sku: string | null
  categoryId: string | null
  categoryName: string | null
  unitId: string | null
  purchaseUnitId: string | null
  purchaseConversion: string
  purchasePrice: string
  minOrderQty: string
  pricing: Pricing
  markupPct: string | null
  markedUpPrice: string | null
  preferredVendorId: string | null
  stocked: boolean
  taxable: boolean
  /** If a job uses part of a skid, it pays for — what it uses, or the whole skid. */
  costBasis: 'used' | 'whole'
  notes: string | null
  orgUnitIds: string[]
  version?: number
}

const BLANK: MaterialForm = {
  name: '', customerName: null, sku: null, categoryId: null, categoryName: null, unitId: null,
  purchaseUnitId: null, purchaseConversion: '1', purchasePrice: '0', minOrderQty: '0',
  pricing: 'company', markupPct: null, markedUpPrice: null, preferredVendorId: null,
  stocked: false, taxable: true, costBasis: 'used', notes: null, orgUnitIds: [],
}

/**
 * What carries over to the next one when you press Save & add another.
 *
 * Somebody entering a supplier's price list is entering forty rows that
 * differ in a name and a number. Clearing the category and the units every
 * time is the difference between a form and a punishment. The fields that
 * carry say so in their help text, so nothing is inherited silently.
 */
const CARRIED = [
  'categoryId', 'categoryName', 'preferredVendorId', 'unitId', 'purchaseUnitId',
  'purchaseConversion', 'pricing', 'markupPct', 'orgUnitIds', 'stocked', 'taxable', 'costBasis',
] as const

interface RecentCost { average: number; lines: number; driftPct: number }

interface Purchase {
  purchaseOrderId: string
  reference: string
  status: string
  qty: string
  unitCost: string
  receivedQty: string | null
  /** Set when it was ordered in the vendor's unit (step 12): `3 skids × $486.50`. */
  buyQty: string | null
  buyPrice: string | null
  buyUnitName: string | null
  vendorName: string
  orderedAt: string
  receiptFileId: string | null
  receiptVerifiedAt: string | null
}

const number = (value: unknown): number | null =>
  value === null || value === undefined || value === ''
    ? null
    : Number(String(value).replace(/[^0-9.-]/g, ''))

export function MaterialForm({
  itemId, canEdit, available, onClose, onSaved, initialName, layer,
}: {
  itemId: string | null
  /** A new material's Name, from what was typed into the picker that made it (cb-04 · 3). */
  initialName?: string
  /** Over the picker that opened it, the way `RecordLookup` stacks its forms at 70. */
  layer?: number
  canEdit: boolean
  /** The branches this person can see, for Where it can be used. */
  available: OrgUnitSummary[]
  onClose: () => void
  /**
   * What just happened, for the list's toast.
   *
   * `again` is true when the sheet stayed open for the next one, and `id` is
   * the material that was written — so the toast can offer to open it
   * (`record-flyouts.md` §1.8) without this form owning a toast of its own.
   */
  onSaved: (result: { message: string; again: boolean; id: string | null }) => void
}) {
  const [form, setForm] = useState<MaterialForm | null>(
    itemId ? null : { ...BLANK, name: initialName?.trim() ?? BLANK.name })
  /** What was loaded, so "has anything changed" is a comparison, not a flag. */
  const [saved, setSaved] = useState<MaterialForm | null>(itemId ? null : { ...BLANK })
  const lookups = useCatalogLookups()
  const [itemMarkup, setItemMarkup] = useState<number | null>(null)
  const companyMarkup = itemMarkup ?? lookups.companyMarkupPct
  /* Try it is never saved; null follows the conversion until somebody types. */
  const [tryQty, setTryQty] = useState<string | null>(null)
  const phone = usePhone()
  const [recentCost, setRecentCost] = useState<RecentCost | null>(null)
  const [purchases, setPurchases] = useState<Purchase[]>([])
  const [priceHistory, setPriceHistory] = useState<Record<string, string>[]>([])
  const [stock, setStock] = useState<{ received: number; used: number; onHand: number } | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [carried, setCarried] = useState(false)
  const body = useRef<HTMLDivElement>(null)

  // A new material starts on "each" if there is one, because most things are.
  useEffect(() => {
    if (itemId || lookups.units.length === 0) return
    const each = lookups.units.find((unit) => unit.code === 'ea')?.id ?? null
    setForm((current) => (current && !current.unitId ? { ...current, unitId: each } : current))
    setSaved((current) => (current && !current.unitId ? { ...current, unitId: each } : current))
  }, [itemId, lookups.units])

  const load = useCallback(async () => {
    if (!itemId) return

    try {
      const payload = await request<{
        item: Record<string, string | boolean | null>
        orgUnits: { orgUnitId: string }[]
        purchaseHistory: Purchase[]
        priceHistory: Record<string, string>[]
        stock?: { received: number; used: number; onHand: number } | null
        price: { appliedMarkupPct: number; usedCompanyDefault: boolean }
        recentCost: RecentCost | null
      }>(`/items/${itemId}`)

      const item = payload.item as unknown as MaterialForm
      const loaded: MaterialForm = {
        ...BLANK,
        ...item,
        /*
         * The stored row says which price is set; the form asks which one is
         * wanted, which is the same fact read the other way round.
         */
        pricing: item.markedUpPrice !== null ? 'fixed'
          : item.markupPct !== null ? 'markup' : 'company',
        orgUnitIds: payload.orgUnits.map((row) => row.orgUnitId),
        /* `486.50` and `520`, not the six places a numeric column arrives with. */
        purchaseConversion: amountText(item.purchaseConversion) || '1',
        purchasePrice: amountText(item.purchasePrice, true),
        minOrderQty: amountText(item.minOrderQty),
        markupPct: item.markupPct === null ? null : amountText(item.markupPct),
        markedUpPrice: item.markedUpPrice === null ? null : amountText(item.markedUpPrice, true),
      }
      setForm(loaded)
      setSaved(loaded)
      if (payload.price.usedCompanyDefault) setItemMarkup(payload.price.appliedMarkupPct)
      setRecentCost(payload.recentCost)
      setPurchases(payload.purchaseHistory)
      setPriceHistory(payload.priceHistory)
      setStock(payload.stock ?? null)
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That material could not be loaded.')
    }
  }, [itemId])

  useEffect(() => { void load() }, [load])

  const patch = (changes: Partial<MaterialForm>) => {
    setForm((current) => (current ? { ...current, ...changes } : current))
    // A field that was wrong stops being wrong the moment it is touched.
    setErrors((current) => {
      const next = { ...current }
      for (const key of Object.keys(changes)) delete next[key]
      return next
    })
  }

  const unitCode = (id: string | null, fallback: string) =>
    lookups.units.find((unit) => unit.id === id)?.code ?? fallback

  const buys = form ? unitCode(form.purchaseUnitId, unitCode(form.unitId, 'unit')) : 'unit'
  const allocates = form ? unitCode(form.unitId, 'unit') : 'unit'

  /*
   * What the form is asking for right now, before anything is saved — so the
   * margin moves as somebody types rather than after a round trip.
   */
  const live = useMemo(() => {
    if (!form) return { cost: 0, sell: 0 }
    const conversion = number(form.purchaseConversion) || 1
    const cost = (number(form.purchasePrice) ?? 0) / conversion
    // A markup lands on a price in cents, as `priceItem` and `SELL_PRICE_SQL`
    // round it: the quote charges $1.26 a sq ft, not $1.263 (Board 1).
    const sell = form.pricing === 'fixed'
      ? number(form.markedUpPrice) ?? 0
      : Math.round(cost * (1 + (form.pricing === 'markup'
        ? number(form.markupPct) ?? 0
        : companyMarkup) / 100) * 100) / 100
    return { cost, sell }
  }, [form, companyMarkup])

  /** Has anything actually changed — not "has anything been typed in". */
  const changes = useMemo(() => {
    if (!form || !saved) return 0
    return Object.keys(form).filter((key) => {
      const now = form[key]
      const was = saved[key]
      if (Array.isArray(now) && Array.isArray(was)) {
        return now.length !== was.length || now.some((one, at) => one !== was[at])
      }
      return now !== was
    }).length
  }, [form, saved])

  /**
   * What is wrong, checked on the way out rather than as somebody types.
   *
   * §1.7: Save is never disabled — pressing it takes you to the first thing
   * that needs attention, which is the whole reason to leave it live.
   */
  function check(next: MaterialForm): Record<string, string> {
    const found: Record<string, string> = {}
    if (!next.name.trim()) found.name = 'A material needs a name'
    if (!next.unitId) found.unitId = 'Say what a job is charged in'
    if ((number(next.purchaseConversion) ?? 0) <= 0) {
      found.purchaseConversion = 'One purchase unit has to yield something'
    }
    if (next.pricing === 'fixed' && (number(next.markedUpPrice) ?? 0) < live.cost) {
      found.markedUpPrice = 'That is below what it costs'
    }
    return found
  }

  async function save(again: boolean) {
    if (!form) return
    const found = check(form)
    setErrors(found)
    if (Object.keys(found).length > 0) {
      // Take them to it rather than telling them there is one.
      const first = body.current?.querySelector<HTMLElement>('[aria-invalid="true"]')
      first?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      first?.focus({ preventScroll: true })
      return
    }

    setBusy(true)
    setProblem(null)
    try {
      const written = await request<{ id?: string }>(itemId ? `/items/${itemId}` : '/items', {
        method: itemId ? 'PUT' : 'POST',
        body: {
          name: form.name,
          customerName: form.customerName?.trim() || null,
          sku: form.sku || null,
          categoryId: form.categoryId || null,
          unitId: form.unitId,
          purchaseUnitId: form.purchaseUnitId || null,
          purchaseConversion: number(form.purchaseConversion) ?? 1,
          purchasePrice: number(form.purchasePrice) ?? 0,
          minOrderQty: number(form.minOrderQty) ?? 0,
          /*
           * Exactly one, decided by the segmented control rather than by
           * which box was left empty.
           */
          markupPct: form.pricing === 'markup' ? number(form.markupPct) ?? 0 : null,
          markedUpPrice: form.pricing === 'fixed' ? number(form.markedUpPrice) ?? 0 : null,
          preferredVendorId: form.preferredVendorId || null,
          stocked: form.stocked,
          taxable: form.taxable,
          costBasis: form.costBasis,
          notes: form.notes || null,
          orgUnitIds: form.orgUnitIds,
          ...(itemId ? { version: form.version ?? 0 } : {}),
        },
      })

      if (again) {
        /* Keep what repeats, clear what does not, and say which is which. */
        const next = { ...BLANK } as MaterialForm
        for (const key of CARRIED) (next as Record<string, unknown>)[key] = form[key]
        setForm(next)
        setSaved(next)
        setCarried(true)
        onSaved({ message: `Added ${form.name}.`, again: true, id: written.id ?? null })
        window.setTimeout(() => {
          body.current?.querySelector<HTMLInputElement>('input')?.focus()
        }, 20)
        return
      }

      onSaved({
        message: itemId ? `Saved ${form.name}.` : `Added ${form.name}.`,
        again: false,
        id: itemId ?? written.id ?? null,
      })
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const title = itemId ? (form?.name || 'Material') : 'New material'
  const allocUnit = lookups.units.find((unit) => unit.id === form?.unitId)
  const buyUnit = lookups.units.find((unit) => unit.id === (form?.purchaseUnitId ?? form?.unitId))
  const rule: BuyingRule = {
    buyUnit: buyUnit?.code ?? buys, buyUnitName: buyUnit?.name ?? buys,
    allocUnit: allocUnit?.code ?? allocates, allocUnitName: allocUnit?.name ?? allocates,
    per: number(form?.purchaseConversion) || 1,
    buyPrice: number(form?.purchasePrice) ?? 0,
    minOrder: number(form?.minOrderQty) ?? 0,
    basis: form?.costBasis ?? 'used',
  }
  const allocOne = buyingUnitWord(rule.allocUnit, rule.allocUnitName, 1)
  const allocMany = buyingUnitWord(rule.allocUnit, rule.allocUnitName, 2)
  const buyName = rule.buyUnitName
  // On the unit, not in cents: a cost of $0.9356 rounded to $0.94 first read
  // 25.4% where the quote's own line makes 25.7% (Board 1).
  const margin = marginPctOf(Math.round(live.sell * 1e6), Math.round(live.cost * 1e6))
  const autoQty = formatNumber(Math.round(rule.per * 2.3) || 1)
  const errorCount = Object.keys(errors).length

  return (
    <CatalogSheet
      kind="item"
      tile="item"
      title={title}
      dirty={canEdit && changes > 0}
      changeCount={changes}
      busy={busy}
      canEdit={canEdit}
      isNew={!itemId}
      onClose={onClose}
      layer={layer}
      onSave={(again) => void save(again)}
      note={errorCount > 0
        ? (errorCount === 1 ? 'One field needs attention' : `${errorCount} fields need attention`)
        : undefined}
    >
      {!form
        ? <p className="m-0 text-cell text-text-muted">Loading…</p>
        : (
          <div ref={body} className="flex flex-col gap-3.5">
            {problem && <Band tone="amber">{problem}</Band>}

            {/* --- what it is ------------------------------------------- */}
            <FormSection title={WHAT_IT_IS} />
            <FormGrid>
              <Wide>
                <Field label={NAME} required error={errors.name}>
                  {({ id, describedBy, invalid }) => (
                    <TextInput
                      id={id}
                      describedBy={describedBy}
                      invalid={invalid}
                      value={form.name}
                      placeholder="Hardwood mulch, bagged"
                      disabled={!canEdit}
                      onChange={(event) => patch({ name: event.target.value })}
                    />
                  )}
                </Field>
              </Wide>
              <CustomerNameField value={form.customerName ?? ''} name={form.name}
                disabled={!canEdit}
                onChange={(customerName) => patch({ customerName: customerName || null })} />

              <Field label={SKU} hint={SKU_HINT}>
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    value={form.sku ?? ''}
                    disabled={!canEdit}
                    onChange={(event) => patch({ sku: event.target.value || null })}
                  />
                )}
              </Field>

              {/*
                * The one Aaron asked for: type a category, and if it is not
                * there the last row makes it. No trip to another tab, and
                * nothing typed above is lost on the way.
                */}
              <Field
                label={CATEGORY}
                hint={carried && form.categoryId ? 'kept from the last one' : undefined}
              >
                {({ id, describedBy }) => (
                  <ComboInput
                    id={id}
                    describedBy={describedBy}
                    label={CATEGORY}
                    thing="category"
                    placeholder="Mulch & soil"
                    disabled={!canEdit}
                    value={form.categoryId
                      ? {
                        id: form.categoryId,
                        name: lookups.categories.find((one) => one.id === form.categoryId)?.name
                          ?? form.categoryName ?? '',
                      }
                      : null}
                    onPick={(picked) => patch({
                      categoryId: picked.id, categoryName: picked.name,
                    })}
                    search={(query) => lookups.categories
                      .filter((one) => one.name.toLowerCase()
                        .includes(query.trim().toLowerCase()))
                      .slice(0, 8)
                      .map((one) => ({ id: one.id, name: one.name }))}
                    onCreate={canEdit
                      ? (name) => createCategory(name, lookups, setProblem) : undefined}
                  />
                )}
              </Field>

              <Wide>
                <Field
                  label={USUAL_VENDOR}
                  hint={lookups.vendors.length === 0
                    ? 'vendors arrive with the first purchase order'
                    : carried && form.preferredVendorId ? 'kept from the last one' : undefined}
                >
                  {({ id, describedBy }) => (
                    <SelectInput
                      id={id}
                      describedBy={describedBy}
                      label={USUAL_VENDOR}
                      placeholder="None yet"
                      value={form.preferredVendorId}
                      disabled={!canEdit}
                      clearable
                      clearLabel="None yet"
                      onChange={(value) => patch({ preferredVendorId: value })}
                      options={lookups.vendors.map((row) => ({
                        value: row.id, label: row.name,
                      }))}
                    />
                  )}
                </Field>
              </Wide>
            </FormGrid>

            {/* --- what it costs ---------------------------------------- */}
            <FormSection title={WHAT_IT_COSTS} sub={WHAT_IT_COSTS_SUB} />

            <PairField
              joiner="at"
              left={(
                <Field label={BUY_LABEL.item} hint={carried ? 'kept from the last one' : undefined}>
                  {({ id }) => (
                    <UnitSelect
                      id={id}
                      label={BUY_LABEL.item}
                      units={lookups.units}
                      value={form.purchaseUnitId ?? form.unitId}
                      disabled={!canEdit}
                      onChange={(value) => patch({ purchaseUnitId: value })}
                      onCreate={canEdit ? (name) => void newUnit(name, 'purchaseUnitId') : undefined}
                    />
                  )}
                </Field>
              )}
              right={(
                <Field label={costPerLabel('item', buyName)}>
                  {({ id }) => (
                    <MoneyInput
                      id={id}
                      value={form.purchasePrice}
                      disabled={!canEdit}
                      onChange={(event) => patch({ purchasePrice: event.target.value })}
                    />
                  )}
                </Field>
              )}
            />

            <PairField
              joiner="="
              left={(
                <Field label={ALLOCATED_IN} required error={errors.unitId}>
                  {({ id, invalid }) => (
                    <UnitSelect
                      id={id}
                      label={ALLOCATED_IN}
                      units={lookups.units}
                      value={form.unitId}
                      invalid={invalid}
                      disabled={!canEdit}
                      onChange={(value) => patch({ unitId: value })}
                      onCreate={canEdit ? (name) => void newUnit(name, 'unitId') : undefined}
                    />
                  )}
                </Field>
              )}
              right={(
                <Field label={perLabel(allocMany, buyName)} error={errors.purchaseConversion}>
                  {({ id, describedBy, invalid }) => (
                    <NumberInput
                      id={id}
                      describedBy={describedBy}
                      invalid={invalid}
                      unit={allocMany}
                      value={form.purchaseConversion}
                      disabled={!canEdit}
                      onChange={(event) => patch({ purchaseConversion: event.target.value })}
                    />
                  )}
                </Field>
              )}
            />

            {/* What those four numbers mean, in one sentence and a worked example. */}
            <Band tone="green" title={buyingSentence(rule)}>{BAND_HINT.item}</Band>

            <FormGrid>
              <Field label={MINIMUM_LABEL.item} hint={MINIMUM_HINT.item}>
                {({ id, describedBy }) => (
                  <NumberInput
                    id={id}
                    describedBy={describedBy}
                    unit={buyName.toLowerCase()}
                    value={form.minOrderQty}
                    disabled={!canEdit}
                    onChange={(event) => patch({ minOrderQty: event.target.value })}
                  />
                )}
              </Field>

              {/* Only when there is one. A field reading "—" claims the
                  fact exists and this material has none. */}
              {purchases[0] && (
                <Field label={LAST_PAID} hint={purchases[0]!.vendorName}>
                  {({ id, describedBy }) => (
                    <ReadonlyInput
                      id={id}
                      describedBy={describedBy}
                      /* What was paid for one buying unit — the box above it
                         is per buying unit too. A line from before buying
                         units were recorded falls back to its unit cost. */
                      value={`${purchases[0]!.buyPrice !== null
                        ? formatRate(Number(purchases[0]!.buyPrice))
                        : formatUnitCost(Number(purchases[0]!.unitCost))} · `
                        + `${formatRelative(purchases[0]!.orderedAt)}`}
                    />
                  )}
                </Field>
              )}
            </FormGrid>

            {recentCost && Math.abs(recentCost.driftPct) >= 5 && (
              <Band
                tone="amber"
                title={`Recent purchases averaged ${formatRate(recentCost.average)} — `
                  + `${formatNumber(Math.abs(Math.round(recentCost.driftPct)))}% `
                  + `${recentCost.driftPct > 0 ? 'over' : 'under'} this cost.`}
              >
                {' '}
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() => patch({ purchasePrice: String(recentCost.average) })}
                  className="font-semibold underline underline-offset-2"
                >
                  Use {formatRate(recentCost.average)}
                </button>
              </Band>
            )}

            <Field group label={ruleLabel('item', buyName)} hint={ruleHint('item', buyName)}>
              {({ labelId }) => (
                <SegmentedPicker
                  labelledBy={labelId}
                  label={ruleLabel('item', buyName)}
                  value={form.costBasis}
                  onChange={(value) => patch({ costBasis: value })}
                  options={[
                    { value: 'used' as const, label: ruleUsed('item', allocMany) },
                    { value: 'whole' as const, label: ruleWhole(buyName) },
                  ]}
                />
              )}
            </Field>

            {/*
              * cb-18 · 4: on a phone the part-of-a-skid question is the
              * two-way control above and its leftover sentence, then Try it —
              * the desktop's "costed two ways" cards are not drawn (Board 2's
              * reading is a desktop one; the segments are already the choice).
              */}
            <TryItPanel compare={!phone} rule={rule} sellsEach={live.sell} kind="item"
              qty={tryQty ?? autoQty} onQty={setTryQty} allocWord={allocMany} />

            {/* --- what it sells for ------------------------------------ */}
            <FormSection title={WHAT_IT_SELLS_FOR} />
            <PricedByField value={form.pricing} companyPct={companyMarkup} disabled={!canEdit}
              onChange={(mode) => patch({ pricing: mode })} />

            {form.pricing === 'markup' && (
              <FormGrid>
                <Field label={MARKUP_ON_COST}>
                  {({ id }) => (
                    <NumberInput
                      id={id}
                      percent
                      value={form.markupPct ?? ''}
                      disabled={!canEdit}
                      onChange={(event) => patch({ markupPct: event.target.value })}
                    />
                  )}
                </Field>
              </FormGrid>
            )}

            {form.pricing === 'fixed' && (
              <FormGrid>
                <Field
                  label={pricePer(allocOne)}
                  error={errors.markedUpPrice}
                  hint="a fixed price does not follow the cost up, which is the point"
                >
                  {({ id, describedBy, invalid }) => (
                    <MoneyInput
                      id={id}
                      describedBy={describedBy}
                      invalid={invalid}
                      value={form.markedUpPrice ?? ''}
                      disabled={!canEdit}
                      onChange={(event) => patch({ markedUpPrice: event.target.value })}
                    />
                  )}
                </Field>
              </FormGrid>
            )}

            <Readout
              items={[
                { label: costSlash(allocOne), value: formatUnitCost(live.cost) },
                { label: sellsSlash(allocOne), value: formatUnitCost(live.sell) },
                { label: MARGIN, value: percentText(margin), tone: marginTone(margin) },
              ]}
            />

            {/* --- where, and what kind of thing it is ------------------- */}
            <WhereAndOptions
              available={available}
              orgUnitIds={form.orgUnitIds}
              onOrgUnits={(next) => patch({ orgUnitIds: next })}
              taxable={form.taxable}
              onTaxable={(next) => patch({ taxable: next })}
              disabled={!canEdit}
              extra={(
                <CheckRow
                  label={TRACK_STOCK}
                  sub={TRACK_STOCK_SUB}
                  checked={form.stocked}
                  disabled={!canEdit}
                  onChange={(next) => patch({ stocked: next })}
                />
              )}
            />

            <NotesField value={form.notes ?? ''} disabled={!canEdit}
              onChange={(next) => patch({ notes: next || null })} />

            {/*
              * What this material has actually done, below the form that
              * describes it.
              *
              * The board draws a *new* material, which has no history — but
              * an existing one does, and it is the point of opening one: "why
              * was last spring's mulch cheaper" is a question somebody is
              * asked in front of a customer. It moves to a record page when
              * materials get one; until then it lives here rather than
              * nowhere.
              */}
            {stock && <StockCard name={form.name} stock={stock} rule={rule} />}

            {purchases.length > 0 && <PurchaseHistory purchases={purchases} allocUnit={rule.allocUnit} />}

            {priceHistory.length > 0 && (
              <History
                title="Price history"
                blurb="Every change, kept. Nothing here is ever overwritten."
                rows={priceHistory.map((row) => ({
                  when: formatRelative(row.effectiveFrom!),
                  what: `${formatRate(Number(row.unitCost ?? 0))} → `
                    + `${formatRate(Number(row.sellPrice))} per ${allocates}`,
                  sub: row.reason ?? (row.markedUpPrice
                    ? 'Fixed price'
                    : `${Math.round(Number(row.markupPct ?? 0))}% markup`),
                }))}
              />
            )}
          </div>
        )}
    </CatalogSheet>
  )

  async function newUnit(name: string, key: 'purchaseUnitId' | 'unitId') {
    const unit = await createUnit(name, lookups, setProblem)
    if (unit) patch({ [key]: unit.id } as Partial<MaterialForm>)
  }
}

/**
 * **Stock** (Board 2's right-hand card): allocation units first, the
 * buying-unit equivalent beside them muted — `360 sq ft · 0.69 skid` —
 * because the yard counts what it holds in the unit jobs use, and the person
 * reordering thinks in skids. `buyingStock` is the one translation.
 */
function StockCard({ name, stock, rule }: {
  name: string
  stock: { received: number; used: number; onHand: number }
  rule: BuyingRule
}) {
  const alloc = buyingUnitWord(rule.allocUnit, rule.allocUnitName, 2)
  const row = (label: string, qty: number, accent = false, equivalent = true) => (
    <div className="flex items-baseline gap-2 border-b border-line-soft px-3 py-2 last:border-b-0">
      <span className="min-w-0 flex-1 text-cell text-text-muted">{label}</span>
      <span className="tnum text-cell">
        <b className={accent ? 'text-accent' : 'text-text'}>{formatNumber(qty)} {alloc}</b>
        {equivalent && buyingStock(qty, rule) && (
          <span className="text-text-faint"> · {buyingStock(qty, rule)}</span>
        )}
      </span>
    </div>
  )
  return (
    <div data-stock-card="" className="flex flex-col gap-2">
      <SectionLabel>{STOCK_TITLE(name)}</SectionLabel>
      <Card className="overflow-hidden p-0">
        {row(STOCK_RECEIVED, stock.received)}
        {row(STOCK_USED, stock.used, false, false)}
        {row(STOCK_LEFT, stock.onHand, true)}
      </Card>
    </div>
  )
}

/** Every purchase of this item, with the visit that proves it. */
function PurchaseHistory({ purchases, allocUnit }: { purchases: Purchase[]; allocUnit: string }) {
  const alloc = (n: number) => buyingUnitWord(allocUnit, undefined, n)
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline gap-2">
        <SectionLabel>Purchase history</SectionLabel>
        <span className="text-sub text-text-faint">
          What it has actually been bought for, which is not always what we say it costs.
        </span>
      </div>
      <Card className="overflow-hidden p-0">
        {purchases.map((purchase, index) => (
          <div key={`${purchase.purchaseOrderId}-${index}`}
            className="flex items-baseline gap-2 border-b border-line-soft px-3 py-2 last:border-b-0">
            <span className="min-w-0 flex-1">
              <span className="block text-cell text-text">
                {/* A line's quantity is in the unit a job uses; the vendor's
                    unit is said only where the order was placed in it. */}
                {purchase.buyQty !== null
                  ? poLineWords(Number(purchase.buyQty), purchase.buyUnitName ?? '',
                    Number(purchase.buyPrice), Number(purchase.qty), allocUnit)
                  : `${formatNumber(Number(purchase.qty))} ${alloc(Number(purchase.qty))} at ${formatUnitCost(Number(purchase.unitCost))}`}
              </span>
              <span className="block text-sub text-text-muted">
                {purchase.vendorName} · {purchase.reference}
              </span>
            </span>
            <span className="shrink-0 text-right">
              <span className="block text-sub text-text-faint">
                {formatRelative(purchase.orderedAt)}
              </span>
              {purchase.receiptFileId
                ? (
                  <Tag tone={purchase.receiptVerifiedAt ? 'ok' : 'plain'}>
                    {purchase.receiptVerifiedAt ? 'Receipt verified' : 'Receipt'}
                  </Tag>
                )
                : <span className="block text-sub text-text-faint">No receipt</span>}
            </span>
          </div>
        ))}
      </Card>
    </div>
  )
}

function History({
  title, blurb, rows,
}: {
  title: string
  blurb: string
  rows: { when: string; what: string; sub: string }[]
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline gap-2">
        <SectionLabel>{title}</SectionLabel>
        <span className="text-sub text-text-faint">{blurb}</span>
      </div>
      <Card className="overflow-hidden p-0">
        {rows.map((row, index) => (
          <div key={index}
            className="flex items-baseline gap-2 border-b border-line-soft px-3 py-2 last:border-b-0">
            <span className="min-w-0 flex-1">
              <span className="block text-cell text-text">{row.what}</span>
              <span className="block text-sub text-text-muted">{row.sub}</span>
            </span>
            <span className="shrink-0 text-sub text-text-faint">{row.when}</span>
          </div>
        ))}
      </Card>
    </div>
  )
}
